import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import DashboardShell from "@/components/DashboardShell";
import { ChevronLeft, ChevronRight, MapPin, Clock, User, Loader2 } from "lucide-react";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const WEEKDAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

function toIsoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function buildCalendarGrid(year, month) {
  // month is 0-indexed
  const first = new Date(year, month, 1);
  const startWeekday = first.getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export default function AdminCalendarPage() {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [scheduled, setScheduled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(toIsoDate(today));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/jobs/scheduled`, { withCredentials: true });
      setScheduled(data);
    } catch (e) {
      toast.error("Failed to load calendar");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const byDate = useMemo(() => {
    const map = {};
    scheduled.forEach((j) => {
      if (!j.scheduled_date) return;
      (map[j.scheduled_date] = map[j.scheduled_date] || []).push(j);
    });
    return map;
  }, [scheduled]);

  const cells = useMemo(() => buildCalendarGrid(cursor.getFullYear(), cursor.getMonth()), [cursor]);
  const todayIso = toIsoDate(today);

  const monthName = `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;
  const dayJobs = (byDate[selectedDate] || []).slice().sort((a, b) => (a.scheduled_time || "").localeCompare(b.scheduled_time || ""));

  return (
    <DashboardShell>
      <div className="mb-8" data-testid="admin-calendar-page">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500">Admin</div>
        <h1 className="mt-2 text-4xl font-extrabold text-stone-900" style={{ fontFamily: "Outfit" }}>
          Calendar
        </h1>
        <p className="mt-2 text-stone-600">
          Every job pinned to a date. Tap a day to see what's on the schedule.
        </p>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center">
          <Loader2 className="w-10 h-10 animate-spin text-brand-600" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 bg-white rounded-3xl border border-stone-200 p-5 md:p-7">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-2xl font-bold text-stone-900" style={{ fontFamily: "Outfit" }} data-testid="calendar-month">
                {monthName}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
                  data-testid="cal-prev-btn"
                  className="w-9 h-9 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center"
                  aria-label="Previous month"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => {
                    setCursor(new Date(today.getFullYear(), today.getMonth(), 1));
                    setSelectedDate(todayIso);
                  }}
                  data-testid="cal-today-btn"
                  className="px-3 h-9 rounded-full text-xs font-bold bg-stone-100 hover:bg-stone-200"
                >
                  Today
                </button>
                <button
                  onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
                  data-testid="cal-next-btn"
                  className="w-9 h-9 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center"
                  aria-label="Next month"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-2">
              {WEEKDAYS.map((w) => (
                <div key={w} className="text-center text-[10px] font-bold uppercase tracking-[0.15em] text-stone-500 py-2">
                  {w}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1.5" data-testid="calendar-grid">
              {cells.map((d, i) => {
                if (!d) return <div key={`pad-${i}`} className="aspect-square" />;
                const iso = toIsoDate(d);
                const jobs = byDate[iso] || [];
                const isToday = iso === todayIso;
                const isSelected = iso === selectedDate;
                return (
                  <button
                    key={iso}
                    onClick={() => setSelectedDate(iso)}
                    data-testid={`cal-day-${iso}`}
                    className={`aspect-square rounded-xl border text-left p-2 flex flex-col transition-all relative ${
                      isSelected
                        ? "bg-brand-600 border-brand-700 text-white shadow-md"
                        : isToday
                        ? "bg-brand-50 border-brand-200 text-stone-900 hover:bg-brand-100"
                        : "bg-white border-stone-200 hover:border-stone-300 text-stone-700"
                    }`}
                  >
                    <span className={`text-sm font-bold ${isSelected ? "text-white" : isToday ? "text-brand-800" : ""}`}>
                      {d.getDate()}
                    </span>
                    {jobs.length > 0 && (
                      <div className="mt-auto flex flex-wrap gap-0.5">
                        {jobs.slice(0, 3).map((j) => (
                          <span
                            key={j.id}
                            className={`w-1.5 h-1.5 rounded-full ${
                              isSelected ? "bg-white" : "bg-brand-600"
                            }`}
                          />
                        ))}
                        {jobs.length > 3 && (
                          <span className={`text-[9px] font-bold leading-none ${isSelected ? "text-white" : "text-brand-700"}`}>
                            +{jobs.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="bg-white rounded-3xl border border-stone-200 p-5 md:p-6 sticky top-6" data-testid="day-detail-panel">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-700 mb-2">
                Selected day
              </div>
              <h3 className="text-2xl font-bold text-stone-900" style={{ fontFamily: "Outfit" }}>
                {new Date(selectedDate + "T00:00:00").toLocaleDateString(undefined, {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                })}
              </h3>
              <div className="mt-1 text-sm text-stone-500">
                {dayJobs.length === 0 ? "No jobs pinned" : `${dayJobs.length} job${dayJobs.length > 1 ? "s" : ""}`}
              </div>

              <div className="mt-5 space-y-3">
                {dayJobs.length === 0 ? (
                  <Link
                    to="/admin/jobs"
                    className="block text-center text-sm font-bold text-brand-700 hover:text-brand-800 py-6 rounded-2xl border-2 border-dashed border-stone-200 hover:border-brand-200 hover:bg-brand-50 transition-all"
                    data-testid="empty-day-cta"
                  >
                    + Pin a job from All Jobs
                  </Link>
                ) : (
                  dayJobs.map((j, i) => (
                    <Link
                      to={`/dashboard/jobs/${j.id}`}
                      key={j.id}
                      data-testid={`day-job-${i}`}
                      className="block p-4 rounded-2xl bg-stone-50 hover:bg-white border border-stone-100 hover:border-stone-300 hover:shadow-sm transition-all"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-bold text-stone-900 truncate" style={{ fontFamily: "Outfit" }}>
                          {j.title}
                        </span>
                        {j.scheduled_time && (
                          <span className="inline-flex items-center gap-1 text-xs font-bold text-brand-700 bg-brand-100 px-2 py-0.5 rounded-full flex-shrink-0">
                            <Clock className="w-3 h-3" />
                            {j.scheduled_time}
                          </span>
                        )}
                      </div>
                      <div className="mt-1.5 flex items-center gap-1.5 text-xs text-stone-600">
                        <User className="w-3 h-3" />
                        <span className="truncate">{j.customer_name}</span>
                      </div>
                      {j.address && (
                        <div className="mt-1 flex items-center gap-1.5 text-xs text-stone-500">
                          <MapPin className="w-3 h-3" />
                          <span className="truncate">{j.address}</span>
                        </div>
                      )}
                      {j.admin_notes && (
                        <div className="mt-2 text-xs text-stone-700 bg-white rounded px-2 py-1.5 border border-stone-200">
                          {j.admin_notes}
                        </div>
                      )}
                    </Link>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}
